import type { Permission } from '@hostesa/auth-client';
import { useAuth } from '@hostesa/auth-client';
import type { FeatureFlag } from '@hostesa/feature-flags';
import { useFlag } from '@hostesa/feature-flags';
import type { ReactNode } from 'react';
import { useHotelContext } from '../lib/hotel-context';
import { NoAccessPage } from '../routes/standalone-pages';

/**
 * Per-route RBAC + ABAC + feature-flag gate, applied inside the hotel layout.
 *
 *   allowed = can(permission, scope) && (flag ? flagEnabled : true)
 *
 * `canInScope` enforces both the role permission (RBAC) and the active
 * membership scope (ABAC) against the resolved tenant/hotel. A failure renders
 * the opaque no-access state — the backend remains the authoritative boundary
 * and any 403/404 it returns is handled identically.
 */
export function PermissionRoute({
  permission,
  flag,
  children,
}: {
  permission: Permission;
  flag?: FeatureFlag;
  children: ReactNode;
}) {
  const { canInScope } = useAuth();
  const hotel = useHotelContext();
  const flagEnabled = useFlag(flag ?? 'in_stay_operations');

  const scope = { tenantId: hotel.tenantId, hotelId: hotel.hotelId };
  const allowed = canInScope(permission, scope) && (flag ? flagEnabled : true);

  if (!allowed) return <NoAccessPage />;
  return <>{children}</>;
}
